/**
 * The one place a card's title decides what it is.
 *
 * Card titles are emitted from four modules — `main.ts`, `tensor-view.ts`,
 * `anatomy-map.ts`, `compare-view.ts` — and until this helper existed every one
 * of them wrote `<div class="card-title">`. A `div` is not a heading, so
 * `document.querySelectorAll("h1,h2,h3,h4,h5,h6")` returned an empty list on a
 * fully loaded page and the primary screen-reader navigation mechanism produced
 * nothing at all (WCAG 1.3.1). Four copies of the wrong tag is also why it
 * stayed wrong: there was no single place to fix.
 *
 * **Level 3, always.** The page's outline is fixed: `h1` is the brand lockup in
 * the header, `h2` is the pane subject (the tensor's name, the empty state's
 * headline, the tensor list), and every card sits inside a pane. A card that
 * wanted its own level would be a card that belongs somewhere else.
 *
 * `inner` is HTML, not text — titles legitimately carry a `.card-title-note`
 * span or a `.btn--tiny` control — so callers escape anything that came off the
 * network before it gets here, exactly as they did when they wrote the tag out
 * by hand.
 */

/** A card's title, as the heading it always should have been. */
export function cardTitle(inner: string): string {
  return `<h3 class="card-title">${inner}</h3>`;
}

/**
 * A card title that is also the disclosure control of a `<details>`.
 *
 * The heading goes *inside* the `<summary>` rather than the class going *on*
 * it, and that is a rendering fix as much as a semantic one: `.card-title` is
 * `display: flex`, and Chrome does not paint the `::marker` disclosure triangle
 * on a flex `<summary>`. "All values, as a table" therefore rendered as a bold
 * label with nothing under it and no affordance suggesting otherwise — a
 * section that reads as broken. It is not decoration: the palette validator
 * WARNs that three light-mode series slots sit under 3:1, and plan §12.6 names
 * this table as the relief that obligation is discharged with. The relief
 * existed and was invisible.
 *
 * With the class on an inner heading the `<summary>` keeps `display: list-item`
 * and its triangle; `summary .card-title` in `app.css` puts the heading back
 * inline so it shares the marker's line box.
 */
export function cardSummaryTitle(inner: string): string {
  return `<summary>${cardTitle(inner)}</summary>`;
}
